import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import MovieCard from "@/components/feature/MovieCard";
import { useFavorites } from "@/hooks/useFavorites";
import { useSeo } from "@/hooks/useSeo";
import { movies } from "@/mocks/movies";

export default function Detail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isFavorite, toggleFavorite } = useFavorites();
  const [shareOpen, setShareOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const movie = movies.find((m) => m.id === id);
  const related = movie
    ? movies.filter((m) => m.id !== movie.id && m.genres.some((g) => movie.genres.includes(g))).slice(0, 4)
    : [];

  useSeo({
    title: movie ? `${movie.title}（${movie.year}）${movie.genres.join("·")} - 一本道影视大全` : "影视详情 - 一本道影视大全",
    description: movie ? movie.summary : "影视作品详情页面，提供影片信息、剧情介绍与相关推荐。",
    keywords: movie ? `${movie.title},${movie.genres.join(",")},${movie.type},一本道影视大全` : "影视详情",
  });

  const favorite = movie ? isFavorite(movie.id) : false;

  const handleCopy = () => {
    const url = `${window.location.origin}${window.location.pathname}`;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url).catch(() => {});
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  if (!movie) {
    return (
      <SiteLayout>
        <div className="w-full px-4 md:px-8 py-24 flex flex-col items-center justify-center text-center">
          <span className="w-20 h-20 rounded-full bg-secondary-100 flex items-center justify-center text-secondary-600">
            <i className="ri-file-unknow-line text-4xl"></i>
          </span>
          <h1 className="mt-6 font-heading text-2xl font-black text-foreground-950">抱歉，没有找到这部影视作品</h1>
          <p className="mt-3 text-sm text-foreground-500">它可能已下架或链接有误，去看看其他精彩内容吧</p>
          <div className="flex items-center gap-3 mt-7">
            <Link
              to="/category"
              className="px-6 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-bold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              返回影视分类
            </Link>
            <Link
              to="/"
              className="px-6 py-2.5 rounded-md border border-background-300 text-sm font-medium text-foreground-800 hover:border-primary-300 transition-colors cursor-pointer whitespace-nowrap"
            >
              回到首页
            </Link>
          </div>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <section className="relative w-full h-[360px] md:h-[460px] overflow-hidden bg-primary-950">
        <img
          src={movie.backdrop}
          alt={`${movie.title}剧照`}
          title={`${movie.title} - 一本道影视大全`}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/70 to-primary-950/30"></div>
      </section>

      <div className="w-full px-4 md:px-8 -mt-40 md:-mt-56 relative z-10">
        <div className="flex flex-col md:flex-row gap-6 md:gap-8">
          <div className="w-40 sm:w-48 md:w-56 shrink-0 mx-auto md:mx-0">
            <div className="w-full aspect-[2/3] rounded-lg overflow-hidden border-4 border-background-50 bg-background-200">
              <img
                src={movie.poster}
                alt={`${movie.title}海报`}
                title={`${movie.title} - 一本道影视大全`}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div className="flex-1 min-w-0 pt-2 md:pt-24 lg:pt-28">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className="px-3 py-1 rounded-full bg-accent-500 text-primary-950 text-xs font-bold">{movie.type}</span>
              <span className="px-3 py-1 rounded-full bg-background-50/15 text-background-50 text-xs font-medium border border-background-50/30">
                {movie.region}
              </span>
              {movie.tags.map((t) => (
                <span
                  key={t}
                  className="px-3 py-1 rounded-full bg-background-50/10 text-background-100 text-xs font-medium border border-background-50/25"
                >
                  {t}
                </span>
              ))}
            </div>
            <h1 className="font-heading text-3xl md:text-5xl font-black text-background-50">{movie.title}</h1>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-4 text-background-100">
              <span className="flex items-center gap-1 text-accent-400 font-bold text-lg">
                <i className="ri-star-fill"></i>
                {movie.rating.toFixed(1)}
              </span>
              <span className="text-sm">{movie.year} 年</span>
              <span className="text-sm">{movie.genres.join(" / ")}</span>
              <span className="text-sm">{movie.duration}</span>
              <span className="text-sm">{(movie.views / 10000).toFixed(1)} 万次浏览</span>
            </div>

            <p className="mt-5 text-sm md:text-base text-background-200 leading-relaxed max-w-3xl">{movie.summary}</p>

            <div className="flex flex-wrap items-center gap-3 mt-7">
              <button
                type="button"
                onClick={() => toggleFavorite(movie.id)}
                className={`inline-flex items-center gap-2 px-6 py-3 rounded-md text-sm font-bold transition-colors cursor-pointer whitespace-nowrap ${
                  favorite ? "bg-accent-500 text-primary-950" : "bg-background-50 text-foreground-900 hover:bg-background-100"
                }`}
              >
                <i className={favorite ? "ri-heart-3-fill" : "ri-heart-3-line"}></i>
                {favorite ? "已收藏" : "收藏"}
              </button>
              <button
                type="button"
                onClick={() => setShareOpen(true)}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-background-50/15 text-background-50 text-sm font-semibold border border-background-50/40 hover:bg-background-50/25 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-share-forward-line"></i>
                分享
              </button>
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-background-50/15 text-background-50 text-sm font-semibold border border-background-50/40 hover:bg-background-50/25 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-go-back-line"></i>
                返回
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-12 md:py-16">
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          <div className="flex-1 min-w-0">
            <h2 className="font-heading text-2xl font-black text-foreground-950">剧情简介</h2>
            <div className="mt-5 space-y-4 text-sm md:text-base text-foreground-700 leading-loose">
              <p>{movie.story}</p>
              <p>
                《{movie.title}》由 {movie.director} 执导，{movie.actors.join("、")} 领衔主演，是一部{" "}
                {movie.year} 年出品的{movie.region}
                {movie.type}，以{movie.genres.join("、")}为核心类型，深受观众喜爱。
              </p>
            </div>
          </div>

          <aside className="w-full lg:w-72 shrink-0">
            <div className="rounded-lg border border-background-200 bg-background-100 p-5">
              <h3 className="font-heading text-lg font-black text-foreground-950 mb-4">影视资料</h3>
              <dl className="space-y-3 text-sm">
                {[
                  { label: "导演", value: movie.director },
                  { label: "主演", value: movie.actors.join("、") },
                  { label: "类型", value: movie.type },
                  { label: "地区", value: movie.region },
                  { label: "语言", value: movie.language },
                  { label: "上映时间", value: movie.releaseDate },
                  { label: "片长", value: movie.duration },
                ].map((row) => (
                  <div key={row.label} className="flex gap-3">
                    <dt className="w-16 shrink-0 text-foreground-400">{row.label}</dt>
                    <dd className="flex-1 text-foreground-800">{row.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </aside>
        </div>

        {related.length > 0 && (
          <div className="mt-14">
            <div className="flex items-center justify-between gap-4 mb-6">
              <h2 className="font-heading text-2xl font-black text-foreground-950">相关推荐</h2>
              <Link
                to="/category"
                className="text-sm font-medium text-primary-600 hover:text-accent-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                查看更多
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {related.map((item) => (
                <MovieCard key={item.id} movie={item} />
              ))}
            </div>
          </div>
        )}
      </div>

      {shareOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-primary-950/60" onClick={() => setShareOpen(false)}></div>
          <div className="relative w-full max-w-sm rounded-lg bg-background-50 p-6 animate-fade-up">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-heading text-lg font-black text-foreground-950">分享作品</h3>
              <button
                type="button"
                aria-label="关闭"
                onClick={() => setShareOpen(false)}
                className="w-8 h-8 rounded-md bg-background-100 flex items-center justify-center text-foreground-700 cursor-pointer"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            <p className="text-sm text-foreground-500 mb-3">复制链接分享给好友</p>
            <div className="flex items-center gap-2 p-2 rounded-md bg-background-100 border border-background-200">
              <span className="flex-1 text-xs text-foreground-600 truncate">{movie.title} · 一本道影视大全</span>
              <button
                type="button"
                onClick={handleCopy}
                className="px-3 py-1.5 rounded-md bg-primary-500 text-background-50 text-xs font-bold cursor-pointer whitespace-nowrap"
              >
                {copied ? "已复制" : "复制"}
              </button>
            </div>
            <div className="flex items-center justify-center gap-4 mt-6">
              {["ri-weibo-fill", "ri-wechat-fill", "ri-qq-fill", "ri-link"].map((icon) => (
                <span
                  key={icon}
                  className="w-11 h-11 rounded-full bg-secondary-100 flex items-center justify-center text-secondary-800 hover:bg-accent-100 hover:text-accent-800 transition-colors cursor-pointer"
                >
                  <i className={`${icon} text-lg`}></i>
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </SiteLayout>
  );
}